// Google/Microsoft "sign in with…" buttons shared by SignIn and SignUp (FR-A2). Renders
// nothing until GET /auth/oauth/providers resolves, and only the providers that are actually
// configured server-side — an unconfigured provider has no client secret to complete a real
// exchange with, so showing its button would just be a dead end.
import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { OAuthProvidersResp } from "../api/types";
import { Icon } from "@grackle/ui";

/** `?link=1` requests the "connect an existing account" variant (AccountPage), which requires
 * an authenticated session and lands back on `/account` instead of `/orgs`. */
function startUrl(provider: "google" | "microsoft", link?: boolean): string {
  const q = link ? "?link=1" : "";
  return `/api/v1/auth/oauth/${provider}/start${q}`;
}

export function OAuthButtons({ link, label = "Or continue with" }: { link?: boolean; label?: string }) {
  // Issue #20: the label prop lets callers customize the divider text per context.
  // SignIn: "Or sign in with"; SignUp: "Or continue with" (or "Or create with").
  const [providers, setProviders] = useState<OAuthProvidersResp | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<OAuthProvidersResp>("GET", "/auth/oauth/providers")
      .then((res) => {
        if (!cancelled) setProviders(res);
      })
      .catch(() => {
        if (!cancelled) setProviders({ google: false, microsoft: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!providers || (!providers.google && !providers.microsoft)) return null;

  return (
    <div className="oauth-block">
      <div className="oauth-divider">
        <span>{label}</span>
      </div>
      <div className="oauth-buttons">
        {providers.google ? (
          <a className="gk-btn gk-btn--secondary gk-btn--block" href={startUrl("google", link)}>
            <Icon name="globe" size={18} /> Google
          </a>
        ) : null}
        {providers.microsoft ? (
          <a className="gk-btn gk-btn--secondary gk-btn--block" href={startUrl("microsoft", link)}>
            <Icon name="grid-four" size={18} /> Microsoft
          </a>
        ) : null}
      </div>
    </div>
  );
}
